/* eslint-disable react-hooks/exhaustive-deps */
import { Table, Card, Space, Button, Select, Switch, Spin, Row, Col, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import useTablePagination from "../../../shared/ui/useTablePagination";
import "../../../shared/styles/ListPages.css";

const { Title, Text } = Typography;

const emptyTotals = {
  receivables_main: 0,
  payables_main: 0,
  net_position_main: 0,
};

const PayablesReceivablesReport = () => {
  const { t } = useTranslation();
  const { pagination: receivablesPagination } = useTablePagination();
  const { pagination: payablesPagination } = useTablePagination();
  const [loading, setLoading] = useState(true);
  const [mainCode, setMainCode] = useState("IQD");
  const [receivables, setReceivables] = useState([]);
  const [payables, setPayables] = useState([]);
  const [totals, setTotals] = useState(emptyTotals);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [customerId, setCustomerId] = useState(undefined);
  const [vendorId, setVendorId] = useState(undefined);
  const [includeZero, setIncludeZero] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (customerId != null) params.customer_id = customerId;
      if (vendorId != null) params.vendor_id = vendorId;
      if (includeZero) params.include_zero = 1;
      const res = await apiClient.get("/reports/payables-receivables", { params });
      const d = res.data;
      setMainCode(d.main_currency_code || "IQD");
      setReceivables(Array.isArray(d.receivables) ? d.receivables : []);
      setPayables(Array.isArray(d.payables) ? d.payables : []);
      setTotals(
        d.totals && typeof d.totals === "object" ? { ...emptyTotals, ...d.totals } : emptyTotals
      );
      const fo = d.filter_options || {};
      setCustomerOptions(Array.isArray(fo.customers) ? fo.customers : []);
      setVendorOptions(Array.isArray(fo.vendors) ? fo.vendors : []);
    } catch {
      setReceivables([]);
      setPayables([]);
      setTotals(emptyTotals);
    } finally {
      setLoading(false);
    }
  }, [customerId, vendorId, includeZero]);

  useEffect(() => {
    load();
  }, [load]);

  const receivableColumns = [
    { title: t("name"), dataIndex: "name", key: "name" },
    { title: t("phoneNumber"), dataIndex: "phone_number", key: "phone_number" },
    { title: t("email"), dataIndex: "email", key: "email" },
    {
      title: t("totalOwed"),
      dataIndex: "total_owed",
      key: "total_owed",
      align: "right",
      render: (v) => formatCurrency(v, mainCode),
    },
    {
      title: t("payablesReceivablesReceivable"),
      dataIndex: "receivable_main",
      key: "receivable_main",
      align: "right",
      render: (v) => formatCurrency(v, mainCode),
    },
    {
      title: t("payablesReceivablesCredit"),
      dataIndex: "customer_credit",
      key: "customer_credit",
      align: "right",
      render: (v) => formatCurrency(v, mainCode),
    },
  ];

  const vendorOnlyFilter = vendorId != null && customerId == null;
  const customerOnlyFilter = customerId != null && vendorId == null;
  const showReceivables = !vendorOnlyFilter;
  const showPayables = !customerOnlyFilter;

  const payableColumns = [
    { title: t("name"), dataIndex: "name", key: "name" },
    { title: t("phone"), dataIndex: "phone", key: "phone" },
    {
      title: t("payablesReceivablesVendorBalance"),
      dataIndex: "balance_main",
      key: "balance_main",
      align: "right",
      render: (v) => formatCurrency(v, mainCode),
    },
    {
      title: t("payablesReceivablesPayable"),
      dataIndex: "payable_main",
      key: "payable_main",
      align: "right",
      render: (v) => formatCurrency(v, mainCode),
    },
  ];

  return (
    <div className="list-page">
      <div className="list-page-header" style={{ marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <Title level={4} style={{ margin: 0 }}>
          {t("payablesReceivablesTitle")}
        </Title>
        <Button icon={<ReloadOutlined />} onClick={load}>
          {t("currencyTotalsRefresh")}
        </Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap align="center" size="middle">
          <span>{t("payablesReceivablesCustomerFilter")}</span>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("payablesReceivablesCustomerFilter")}
            style={{ minWidth: 220 }}
            value={customerId}
            onChange={setCustomerId}
            options={customerOptions.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
          />
          <span>{t("payablesReceivablesVendorFilter")}</span>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t("payablesReceivablesVendorFilter")}
            style={{ minWidth: 220 }}
            value={vendorId}
            onChange={setVendorId}
            options={vendorOptions.map((v) => ({
              value: v.id,
              label: v.name,
            }))}
          />
          <Space>
            <Switch checked={includeZero} onChange={setIncludeZero} />
            <Text>{t("payablesReceivablesIncludeZero")}</Text>
          </Space>
        </Space>
      </Card>

      <Spin spinning={loading}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {showReceivables ? (
            <Col xs={24} sm={showPayables ? 8 : 24}>
              <Card size="small">
                <Text type="secondary">{t("payablesReceivablesTotalAR")}</Text>
                <div style={{ fontSize: 18, fontWeight: 600 }}>
                  {formatCurrency(totals.receivables_main, mainCode)}
                </div>
              </Card>
            </Col>
          ) : null}
          {showPayables ? (
            <Col xs={24} sm={showReceivables ? 8 : 24}>
              <Card size="small">
                <Text type="secondary">{t("payablesReceivablesTotalAP")}</Text>
                <div style={{ fontSize: 18, fontWeight: 600 }}>
                  {formatCurrency(totals.payables_main, mainCode)}
                </div>
              </Card>
            </Col>
          ) : null}
          {showReceivables && showPayables ? (
            <Col xs={24} sm={8}>
              <Card size="small">
                <Text type="secondary">{t("payablesReceivablesNet")}</Text>
                <div style={{ fontSize: 18, fontWeight: 600 }}>
                  {formatCurrency(totals.net_position_main, mainCode)}
                </div>
              </Card>
            </Col>
          ) : null}
        </Row>

        {showReceivables ? (
          <Card title={t("payablesReceivablesReceivablesSection")} style={{ marginBottom: showPayables ? 16 : 0 }}>
            <Table
              rowKey={(r) => r.customer_id}
              columns={receivableColumns}
              dataSource={receivables}
              pagination={receivablesPagination}
              scroll={{ x: true }}
            />
          </Card>
        ) : null}

        {showPayables ? (
          <Card title={t("payablesReceivablesPayablesSection")}>
            <Table
              rowKey={(r) => r.vendor_id}
              columns={payableColumns}
              dataSource={payables}
              pagination={payablesPagination}
              scroll={{ x: true }}
            />
          </Card>
        ) : null}
      </Spin>
    </div>
  );
};

export default PayablesReceivablesReport;
